/**
 * Methodology — "How the 360 family works as one method".
 * Renders the canonical, code-derived method DNA from GET /public/method.json so the
 * public story can never drift from the engine. Built on the existing `ek-` tokens.
 */
import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Sparkles, Layers, GitBranch, CheckCircle2, FileDown } from "lucide-react";
import { API_BASE } from "../lib/api";

const bandTone = {
  Emerging: "bg-rose-50 text-rose-700 border-rose-200",
  Developing: "bg-amber-50 text-amber-700 border-amber-200",
  Established: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Advanced: "bg-emerald-100 text-emerald-800 border-emerald-300",
};

export default function Methodology() {
  const [spec, setSpec] = useState(null);
  const [email, setEmail] = useState("");
  const [keepPosted, setKeepPosted] = useState(true);

  useEffect(() => {
    fetch(`${API_BASE}/public/method.json`)
      .then((r) => r.json())
      .then(setSpec)
      .catch(() => setSpec(null));
  }, []);

  // Optional lead capture — NEVER blocks the download.
  const downloadPaper = () => {
    const trimmed = email.trim();
    if (trimmed) {
      fetch(`${API_BASE}/public/methodology/capture`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: trimmed, keep_posted: keepPosted }),
      }).catch(() => {});
    }
    window.open(`${API_BASE}/public/methodology.pdf`, "_blank", "noopener");
  };

  return (
    <div data-testid="methodology-page">
      {/* Hero */}
      <section className="relative ek-gradient-dark text-white overflow-hidden" data-testid="methodology-hero">
        <div className="ek-star-watermark" />
        <div className="ek-vignette-top" />
        <div className="ek-container relative z-10 py-20 md:py-28">
          <div className="ek-eyebrow text-[#A179FA] ek-fade-up">The System of Proof method</div>
          <h1 className="ek-heading text-5xl md:text-6xl lg:text-7xl text-white mt-5 max-w-4xl ek-fade-up" style={{ animationDelay: "60ms" }} data-testid="methodology-headline">
            One discipline. <span className="text-[#A179FA]">Two methodologies.</span>
          </h1>
          <p className="text-zinc-300 font-light text-lg md:text-xl leading-relaxed mt-7 max-w-2xl ek-fade-up" style={{ animationDelay: "120ms" }}>
            Every EKSELENS360 lens and the ENTERPRISE360 engine share the same DNA — the same
            maturity bands, the same decision-grade deliverables, the same eight-phase lifecycle.
            That shared evidence discipline is the System of Proof: one discipline, delivered
            two ways — function view and value-stream view.
          </p>
          <div className="flex flex-wrap gap-4 mt-9 ek-fade-up" style={{ animationDelay: "180ms" }}>
            <Link to="/value-stream-assessment" className="ek-btn ek-btn-primary" data-testid="methodology-vs-cta">
              Start with a free assessment <ArrowRight size={16} />
            </Link>
            <Link to="/the-360-family" className="ek-btn ek-btn-outline-light" data-testid="methodology-family-cta">
              See the 360 family
            </Link>
          </div>

          {/* Optional lead capture — download never blocked */}
          <div className="mt-8 max-w-xl ek-fade-up" style={{ animationDelay: "220ms" }} data-testid="methodology-paper-capture">
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Work email (optional)"
                data-testid="methodology-paper-email"
                className="flex-1 bg-white/10 border border-white/20 rounded px-4 py-3 text-white placeholder-zinc-400 text-[15px] focus:outline-none focus:border-[#A179FA] transition-colors"
              />
              <button onClick={downloadPaper} data-testid="methodology-paper-download"
                className="ek-btn ek-btn-primary inline-flex items-center gap-2 shrink-0 justify-center">
                <FileDown size={16} /> Download paper (PDF)
              </button>
            </div>
            <label className="flex items-center gap-2 mt-3 text-[13px] text-zinc-400 cursor-pointer">
              <input type="checkbox" checked={keepPosted} onChange={(e) => setKeepPosted(e.target.checked)}
                data-testid="methodology-paper-keepposted" className="accent-[#7737F6]" />
              Keep me posted on new thinking (optional — no email required to download)
            </label>
          </div>
        </div>
      </section>

      {/* Method steps */}
      <section className="bg-white ek-section" data-testid="methodology-steps-section">
        <div className="ek-container">
          <div className="ek-eyebrow">The eight-phase lifecycle</div>
          <h2 className="ek-heading text-4xl md:text-5xl mt-4 text-black mb-12">Discover → design → deliver → optimise.</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {(spec?.steps || []).map((s) => (
              <div key={s.step} className="ek-card flex flex-col" data-testid={`methodology-step-${s.step.split(" ")[0]}`}>
                <div className="text-[11px] font-bold tracking-[0.18em] text-[#7737F6] uppercase">{s.step}</div>
                <p className="ek-body text-[14px] mt-3">{s.what}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Shared maturity bands + deliverable spine */}
      <section className="bg-[#F2F2F3] ek-section" data-testid="methodology-bands-section">
        <div className="ek-container grid grid-cols-1 lg:grid-cols-2 gap-12">
          <div>
            <div className="ek-eyebrow">One shared maturity scale</div>
            <h2 className="ek-heading text-3xl md:text-4xl mt-4 text-black mb-6">The same bands everywhere.</h2>
            <div className="space-y-3">
              {(spec?.bands || []).map((b) => (
                <div key={b.band} className={`border rounded-lg p-4 ${bandTone[b.band] || "bg-white border-zinc-200"}`} data-testid={`methodology-band-${b.band}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-semibold">{b.band}</span>
                    <span className="text-[12px] opacity-80">{b.range}</span>
                  </div>
                  <p className="text-[13.5px] mt-1 opacity-90">{b.meaning}</p>
                </div>
              ))}
            </div>
            {spec?.scales?.note && <p className="text-zinc-500 text-[13px] mt-4">{spec.scales.note}</p>}
          </div>
          <div>
            <div className="ek-eyebrow">Decision-grade deliverables</div>
            <h2 className="ek-heading text-3xl md:text-4xl mt-4 text-black mb-6">Every recommendation, the same spine.</h2>
            <div className="border border-zinc-200 bg-white rounded-lg divide-y">
              {(spec?.deliverable_spine || []).map((d, i) => (
                <div key={d.element} className="flex items-start gap-3 px-4 py-3.5" data-testid={`methodology-spine-${i}`}>
                  <span className="w-6 h-6 shrink-0 rounded-full bg-[#7737F6]/10 text-[#7737F6] text-[12px] font-semibold flex items-center justify-center">{i + 1}</span>
                  <div>
                    <div className="text-[14px] font-semibold text-black">{d.element}</div>
                    <div className="text-[13px] text-zinc-600">{d.note}</div>
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-8">
              <div className="ek-eyebrow">Principles</div>
              <ul className="mt-3 space-y-2">
                {(spec?.principles || []).map((p, i) => (
                  <li key={i} className="flex items-start gap-2 text-[14px] text-zinc-700" data-testid={`methodology-principle-${i}`}>
                    <CheckCircle2 size={16} className="text-[#7737F6] shrink-0 mt-0.5" /> {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Family taxonomy */}
      <section className="bg-white ek-section" data-testid="methodology-family-section">
        <div className="ek-container">
          <div className="ek-eyebrow">One family</div>
          <h2 className="ek-heading text-3xl md:text-4xl mt-4 text-black mb-10">
            {spec?.lens_count || 12} functional lenses · {spec?.value_stream_count || 12} value streams · 1 engine.
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <div className="flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-[#7737F6] mb-3">
                <Layers size={14} /> Functional lenses
              </div>
              <div className="flex flex-wrap gap-2">
                {(spec?.lenses || []).map((l) => (
                  <span key={l.code} className="text-[12px] bg-[#F2F2F3] text-zinc-700 border border-zinc-200 rounded-full px-3 py-1" title={l.area} data-testid={`methodology-lens-${l.code}`}>
                    {l.code}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className="flex items-center gap-2 text-[12px] tracking-[0.16em] uppercase text-[#7737F6] mb-3">
                <GitBranch size={14} /> Value streams (engine)
              </div>
              <div className="flex flex-wrap gap-2">
                {(spec?.value_streams || []).map((s) => (
                  <span key={s.id} className={`text-[12px] rounded-full px-3 py-1 border ${s.enabling ? "bg-violet-50 text-[#7737F6] border-violet-200" : "bg-[#F2F2F3] text-zinc-700 border-zinc-200"}`}>
                    {s.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-black text-white ek-section" data-testid="methodology-cta-section">
        <div className="ek-container text-center max-w-3xl">
          <Sparkles size={22} className="text-[#A179FA] mx-auto" />
          <h2 className="ek-heading text-4xl md:text-5xl text-white mt-5">See the method in three minutes.</h2>
          <p className="text-zinc-300 font-light text-lg mt-5">
            Take a free value-stream self-assessment and watch the joined-up method turn your
            answers into a maturity read and a clear next step.
          </p>
          <div className="flex flex-wrap gap-4 justify-center mt-9">
            <Link to="/value-stream-assessment" className="ek-btn ek-btn-primary" data-testid="methodology-final-vs">
              Assess a value stream — free <ArrowRight size={16} />
            </Link>
            <Link to="/enterprise360" className="ek-btn ek-btn-outline-light" data-testid="methodology-final-engine">
              Explore the engine
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
